import React, { useState, JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import styles from "./AuthPage.module.css";
import SignupModal from "../components/auth/SignupModal";

export default function AuthPage(): JSX.Element {
  const [signupOpen, setSignupOpen] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Login Form State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setError("");
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        navigate("/app");
      }
    } catch (err: any) {
      setError(err.message || "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setError("");
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        setError(error.message);
      } else {
        setResetSent(true);
      }
    } catch (err: any) {
      setError(err.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setForgotMode(false);
    setResetSent(false);
    setError("");
  };

  return (
    <div className={styles.welcome}>
      {/* Left Panel: Thematic/Cozy */}
      <div className={styles.leftPanel}>
        <div className={styles.leftContent}>
          <div className={styles.brandHeader}>
            <img src="/favicon.ico" alt="Logo" className={styles.logoIcon} />
            <div className={styles.textBlock}>
              <h1 className={styles.leftTitle}>Gratitude Board</h1>
              <p className={styles.leftSubtitle}>
                Share your gratitude with others.
              </p>
            </div>
          </div>
          <div className={styles.sampleGrid}>
            {/* Sample Post-it Notes */}
            <div className={`${styles.sampleCard} ${styles.cardYellow}`}>
              <h3 className={styles.cardTitle}>Music</h3>
              <p className={styles.cardBody}>How much of a blessing is it to be able to listen with our ears and appreciate with our hearts?</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Grace</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardBlue}`}>
              <img src="/images/flowers.jpg" alt="Flowers" className={styles.cardImage} />
              <p className={styles.cardBody}>I'm so grateful for my friends and the flowers they gave me after my concert!</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Eddy</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardGreen}`}>
              <h3 className={styles.cardTitle}>Family</h3>
              <p className={styles.cardBody}>I'm grateful for going on walks with my parents.</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Alex</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardPink}`}>
              <h3 className={styles.cardTitle}>I'm grateful for ping pong and blueberries.</h3>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Chris</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardPurple}`}>
              <img src="/images/seattle-skyline.jpeg" alt="Seattle Skyline" className={styles.cardImage} />
              <p className={styles.cardBody}>I love Seattle. Grateful for the energy, lights, and calling this place home.</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Jerry</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardOrange}`}>
              <img src="/images/puzzle.jpg" alt="Puzzle" className={styles.cardImage} />
              <p className={styles.cardBody}>My family and I have been working on this puzzle for two weeks—so grateful we finally finished it!</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Emily</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardYellow}`}>
              <img src="/images/night-sky.jpeg" alt="Night Sky" className={styles.cardImage} />
              <h3 className={styles.cardTitle}>Stargazing</h3>
              <p className={styles.cardBody}>I'm grateful for the beautiful stars tonight!</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Sophia</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardBlue}`}>
              <img src="/images/lighthouse.jpeg" alt="Lighthouse" className={styles.cardImage} />
              <h3 className={styles.cardTitle}>Road Trip View</h3>
              <p className={styles.cardBody}>I'm grateful we found this picturesque lighthouse on our way to Monterey!</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Justin</span>
              </div>
            </div>
            <div className={`${styles.sampleCard} ${styles.cardGreen}`}>
              <p className={styles.cardBody}>I'm grateful to be able to celebrate, be joyful, and create joyful moments with others.</p>
              <div className={styles.cardFooter}>
                <span className={styles.cardAuthor}>Sarah</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel: Authentication */}
      <div className={styles.rightPanel}>
        <div className={styles.authContainer}>
          <div className={styles.welcomeText}>
            <h2 className={styles.welcomeTitle}>
              {resetSent ? "Check your email" : forgotMode ? "Reset password" : "Welcome back"}
            </h2>
            <p className={styles.welcomeSubtitle}>
              {resetSent
                ? `We've sent a password reset link to ${email}.`
                : forgotMode
                  ? "Enter your email and we'll send you a reset link."
                  : "Please enter your details to sign in."}
            </p>
          </div>

          {!resetSent && (
            <form className={styles.loginForm} onSubmit={forgotMode ? handleForgotPassword : handleLogin}>
              {error && <div className={styles.errorMessage}>{error}</div>}

              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="email">Email</label>
                <input
                  id="email"
                  className={styles.input}
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {!forgotMode && (
                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="password">Password</label>
                  <input
                    id="password"
                    className={styles.input}
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              <button type="submit" className={styles.primaryButton} disabled={loading}>
                {forgotMode
                  ? (loading ? "Sending..." : "Send reset link")
                  : (loading ? "Logging in..." : "Log in")}
              </button>

              {!forgotMode && (
                <button
                  type="button"
                  className={styles.forgotPasswordLink}
                  onClick={() => { setForgotMode(true); setError(""); }}
                >
                  Forgot password?
                </button>
              )}
            </form>
          )}

          {(forgotMode || resetSent) && (
            <button type="button" className={styles.backLink} onClick={backToLogin}>
              Back to login
            </button>
          )}

          {!forgotMode && !resetSent && (
            <>
              <div className={styles.divider}>
                <span>OR</span>
              </div>

              <button
                type="button"
                className={styles.googleButton}
                onClick={async () => {
                  setLoading(true);
                  await signInWithGoogle();
                }}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>

              <div className={styles.actions}>
                <button className={styles.secondaryButton} onClick={() => setSignupOpen(true)}>
                  Create new account
                </button>
              </div>

              <div className={styles.privacySection} style={{ marginTop: '32px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                <a href="/privacy-copy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>
              </div>
            </>
          )}
        </div>
      </div>

      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  );
}
